import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlined";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutlined";
import StarIcon from "@mui/icons-material/Star";
import StarOutlineIcon from "@mui/icons-material/StarBorder";
import { Box, LinearProgress, Paper, Stack, Typography } from "@mui/material";
import type { DateTime } from "luxon";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import MetaChip from "../../../components/UIToolkit/Badges/MetaChip";
import type { PbjPlanStatus } from "../../../utils/models";
import { formatPlanDate } from "./planValue.utils";

// The CMS star positions, keyed by the star they represent rather than by list index.
const STAR_POSITIONS = [1, 2, 3, 4, 5] as const;

type Props = {
	status: PbjPlanStatus;
	// Aggregated from the actions' own `onTrack` flags — the endpoint reports on-track per lever,
	// not per plan, and an infeasible lever already reports false.
	onTrack: boolean;
	// False while no week has closed anywhere in the plan, when every action's `onTrack` is vacuously
	// true and the aggregate would be a guaranteed green badge rather than an earned one.
	judged: boolean;
	// Already-parsed Luxon values off the progress payload, not ISO strings.
	quarterStart: DateTime;
	quarterEnd: DateTime;
	trackingStartDate: DateTime;
};

export const PlanStatusHeader: FC<Props> = ({ status, onTrack, judged, quarterStart, quarterEnd, trackingStartDate }) => {
	const { t } = useTranslation();
	const span = status.targetPoints - status.startingPoints;
	// With a positive span the bar fills from the score the plan started at toward its target.
	//
	// A plan saved at or ABOVE its own target has no span to fill (a department already past the
	// band floor it was aiming for). Filling the bar unconditionally in that case is wrong the
	// moment the live score drops back below the target — it would read 100% complete beside
	// "5 points to target". With nothing to interpolate, the only honest states are met or not.
	const progressPct =
		span > 0
			? Math.min(100, Math.max(0, ((status.currentPoints - status.startingPoints) / span) * 100))
			: status.currentPoints >= status.targetPoints
				? 100
				: 0;
	const pointsToTarget = status.targetPoints - status.currentPoints;

	return (
		<Paper
			variant="outlined"
			sx={{ p: 3, borderRadius: 2, height: "100%", display: "flex", flexDirection: "column", gap: 2.5 }}
			data-testid="pbj-model-plan-status"
		>
			<Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ justifyContent: "space-between", alignItems: { sm: "flex-start" } }}>
				<Box>
					<Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.6 }}>
						{t("pbj_model.tracker.current_status")}
					</Typography>
					<Stack direction="row" spacing={0.25} sx={{ alignItems: "center", mt: 0.5 }} data-testid="pbj-model-plan-stars">
						{STAR_POSITIONS.map((position) =>
							position <= status.currentStars ? (
								<StarIcon key={position} sx={{ fontSize: 28, color: "warning.main" }} />
							) : (
								<StarOutlineIcon key={position} sx={{ fontSize: 28, color: "text.disabled" }} />
							),
						)}
						<Typography variant="h5" sx={{ fontWeight: 700, ml: 1 }}>
							{status.currentStars}
						</Typography>
					</Stack>
					<Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
						{t("pbj_model.tracker.stars_from_to", { from: status.startingStars, to: status.targetStars })}
					</Typography>
				</Box>
				<Stack spacing={1} sx={{ alignItems: { xs: "flex-start", sm: "flex-end" } }}>
					<MetaChip
						tone={!judged ? "neutral" : onTrack ? "success" : "error"}
						size="medium"
						icon={!judged ? <RemoveCircleOutlineIcon /> : onTrack ? <CheckCircleOutlineIcon /> : <ErrorOutlineIcon />}
						label={t(!judged ? "pbj_model.tracker.not_judged_yet" : onTrack ? "pbj_model.tracker.plan_on_track" : "pbj_model.tracker.plan_off_track")}
						tooltip={!judged ? t("pbj_model.tracker.not_judged_yet_tooltip") : undefined}
						dataTestId="pbj-model-plan-on-track"
					/>
					<Typography variant="caption" color="text.secondary">
						{t("pbj_model.tracker.plan_window", { start: formatPlanDate(quarterStart), end: formatPlanDate(quarterEnd) })}
					</Typography>
					<Typography variant="caption" color="text.secondary">
						{t("pbj_model.tracker.tracking_since", { date: formatPlanDate(trackingStartDate) })}
					</Typography>
				</Stack>
			</Stack>

			<Box sx={{ mt: "auto" }}>
				<Stack direction="row" sx={{ justifyContent: "space-between", mb: 0.75, gap: 1, flexWrap: "wrap" }}>
					<Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
						{t("pbj_model.tracker.points_starting", { value: status.startingPoints.toFixed(0) })}
					</Typography>
					<Typography variant="caption" color="primary.main" sx={{ fontWeight: 700 }} data-testid="pbj-model-plan-current-points">
						{t("pbj_model.tracker.points_current", { value: status.currentPoints.toFixed(0) })}
					</Typography>
					<Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
						{t("pbj_model.tracker.points_target", { value: status.targetPoints.toFixed(0) })}
					</Typography>
				</Stack>
				<LinearProgress
					variant="determinate"
					value={progressPct}
					sx={{ height: 12, borderRadius: 6 }}
					aria-label={t("pbj_model.tracker.points_progress_aria")}
					data-testid="pbj-model-plan-points-bar"
				/>
				<Typography variant="caption" color="text.secondary" sx={{ display: "block", textAlign: "end", mt: 0.75 }}>
					{pointsToTarget > 0
						? t("pbj_model.tracker.points_remaining", { value: pointsToTarget.toFixed(0) })
						: t("pbj_model.tracker.points_target_met", { value: Math.abs(pointsToTarget).toFixed(0) })}
				</Typography>
			</Box>
		</Paper>
	);
};
